import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInstagram, faViber } from '@fortawesome/free-brands-svg-icons';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../contexts/LanguageContext';
import { useSalon } from '../../contexts/SalonContext';
import ImageViewerModal from './ImageViewerModal';
import DressDisplay from './DressDisplay';
import ChangeLog from './ChangeLog';
import { getThumbnailUrl, getImageUrl } from '../../utils/api';
import useScrollLock from '../../hooks/useScrollLock';
import { printTemplateDocument } from '../../utils/printDocument';
import './Salon.css';

const AddEditReservationModal = ({ isOpen, onClose, salonId, onReservationAdded, reservation, mode = 'add', prefillData, userRole = 'member' }) => {
  const { t, getMonthName } = useLanguage();

  // Convert database field name to localized label
  const getLocalizedFieldName = (fieldName) => {
    // Map of database field names to translation keys
    const fieldNameMap = {
      'dress_id': 'dress',
      'reservation_date': 'reservationDate',
      'wedding_date': 'weddingDate',
      'contract_date': 'contractDate',
      'transaction_type': 'transactionType',
      'measurement_datetime': 'measurementDateTime',
      'dress_pickup_datetime': 'pickupDateTime',
      'dress_return_datetime': 'returnDateTime',
      'bride_name': 'brideName',
      'bride_surname': 'brideSurname',
      'bride_telephone': 'brideTelephone',
      'bride_city': 'brideCity',
      'bride_address': 'brideAddress',
      'bride_id_card_number': 'brideIdCardNumber',
      'bride_id_card_city': 'brideIdCardCity',
      'bride_personal_id_number': 'bridePersonalIdNumber',
      'bride_instagram': 'brideInstagram',
      'bride_birth_year': 'brideBirthYear',
      'price': 'price',
      'deposit': 'deposit',
      'voucher_number': 'voucherNumber',
      'venue': 'venue',
      'paid_in_full': 'paidInFull',
      'payment_comment': 'paymentComment',
      'correction_notes': 'correctionNotes',
      'heel_height': 'heelHeight',
      'dress_length': 'dressLength',
      'corset_notes': 'corsetNotes',
      'strap_notes': 'strapNotes',
      'neckline_notes': 'necklineNotes',
      'side_slit_notes': 'sideslitNotes',
      'general_notes': 'generalNotes',
      'veil_size': 'veilSize',
      'glove_size': 'gloveSize',
      'hair_flower_count': 'hairFlowers',
      'handbag_id': 'handbag',
      'coat_id': 'coat',
      'wrap_id': 'wrap',
      'bolero_id': 'bolero',
      'tiara_id': 'tiara',
      'sash_id': 'sash',
      'fur_jacket_id': 'furJacket',
      'veil_id': 'veil',
      'protective_bag_count': 'protectiveBags',
      'small_protective_bag_count': 'smallProtectiveBags',
      'hanger_hook_count': 'hangerHooks',
      'crinoline_size': 'crinolineSize'
    };

    const translationKey = fieldNameMap[fieldName];
    if (translationKey) {
      // Try reservations namespace first
      const reservationKey = `reservations.${translationKey}`;
      const translation = t(reservationKey);
      
      // If translation key doesn't exist (returns the key itself), try common namespace
      if (translation === reservationKey) {
        // Check if it's a common field
        const commonFields = {
          'price': 'price',
          'address': 'address',
          'city': 'city',
          'phone': 'phone',
          'name': 'name',
          'date': 'date'
        };
        if (commonFields[translationKey]) {
          return t(`common.${commonFields[translationKey]}`);
        }
      }
      return translation;
    }
    
    // Fallback: convert snake_case to Title Case if no mapping exists
    return fieldName
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };
  const { salonSettings, currencySymbol } = useSalon();

  const isEditLocked = useMemo(() => {
    if (mode !== 'edit') return false;
    if (userRole === 'owner' || userRole === 'admin') return false;
    const wd = reservation?.wedding_date || reservation?.wedding_date_formatted;
    if (!wd) return false;
    const lockDays = salonSettings.reservation_edit_lock_days?.value ?? 30;
    const diffMs = Date.now() - new Date(wd).getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    return diffDays > lockDays;
  }, [mode, userRole, reservation, salonSettings]);

  const [formData, setFormData] = useState({
    dress_id: '',
    reservation_date: '',
    wedding_date: '',
    contract_date: '',
    transaction_type: 'rental',
    measurement_datetime: '',
    dress_pickup_datetime: '',
    dress_return_datetime: '',
    bride_name: '',
    bride_surname: '',
    bride_telephone: '',
    bride_city: '',
    bride_address: '',
    bride_id_card_number: '',
    bride_id_card_city: '',
    bride_personal_id_number: '',
    bride_instagram: '',
    bride_birth_year: '',
    price: '',
    deposit: '',
    voucher_number: '',
    venue: '',
    paid_in_full: false,
    payment_comment: '',
    correction_notes: '',
    heel_height: '',
    dress_length: '',
    corset_notes: '',
    strap_notes: '',
    neckline_notes: '',
    side_slit_notes: '',
    general_notes: '',
    veil_size: '',
    glove_size: '',
    hair_flower_count: 0,
    handbag_id: '',
    coat_id: '',
    wrap_id: '',
    bolero_id: '',
    tiara_id: '',
    sash_id: '',
    fur_jacket_id: '',
    veil_id: '',
    protective_bag_count: 0,
    small_protective_bag_count: 0,
    hanger_hook_count: 0,
    crinoline_size: 0
  });

  const [accessories, setAccessories] = useState([]);
  const [error, setError] = useState('');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [dressSearchTerm, setDressSearchTerm] = useState('');
  const [dressSearchResults, setDressSearchResults] = useState([]);
  const [isSearchingDresses, setIsSearchingDresses] = useState(false);
  const [referralSources, setReferralSources] = useState([]);
  const [selectedReferralSources, setSelectedReferralSources] = useState([]);
  const [showReferralDropdown, setShowReferralDropdown] = useState(false);
  // Originating appointment id when reservation is being created from an appointment
  const originatingAppointmentIdRef = useRef(null);

  // Lock body scroll when modal is open
  useScrollLock(isOpen);
  const modalBodyRef = useRef(null);
  const [selectedDress, setSelectedDress] = useState(null);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [viewerImages, setViewerImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isLoadingContract, setIsLoadingContract] = useState(false);
  const [contractError, setContractError] = useState('');
  const [availableContracts, setAvailableContracts] = useState([]);
  const [selectedContractId, setSelectedContractId] = useState('');
  const [isLoadingContracts, setIsLoadingContracts] = useState(false);
  const searchTimeoutRef = useRef(null);
  const isSelectingDress = useRef(false);
  const originalReservationRef = useRef(null);
  const shouldScrollToFirstErrorRef = useRef(false);
  const [isChangeLogVisible, setIsChangeLogVisible] = useState(false);

  const formatReservationForForm = (reservationData = {}) => ({
    dress_id: reservationData.dress_id || '',
    reservation_date: reservationData.reservation_date_formatted || reservationData.reservation_date || '',
    wedding_date: reservationData.wedding_date_formatted || reservationData.wedding_date || '',
    contract_date: reservationData.contract_date_formatted || reservationData.contract_date || '',
    transaction_type: reservationData.transaction_type || 'rental',
    measurement_datetime: reservationData.measurement_datetime_formatted || reservationData.measurement_datetime || '',
    dress_pickup_datetime: reservationData.dress_pickup_datetime_formatted || reservationData.dress_pickup_datetime || '',
    dress_return_datetime: reservationData.dress_return_datetime_formatted || reservationData.dress_return_datetime || '',
    bride_name: reservationData.bride_name || '',
    bride_surname: reservationData.bride_surname || '',
    bride_telephone: reservationData.bride_telephone || '',
    bride_city: reservationData.bride_city || '',
    bride_address: reservationData.bride_address || '',
    bride_id_card_number: reservationData.bride_id_card_number || '',
    bride_id_card_city: reservationData.bride_id_card_city || '',
    bride_personal_id_number: reservationData.bride_personal_id_number || '',
    bride_instagram: reservationData.bride_instagram || '',
    bride_birth_year: reservationData.bride_birth_year != null ? String(reservationData.bride_birth_year) : '',
    price: reservationData.price || '',
    deposit: reservationData.deposit || '',
    voucher_number: reservationData.voucher_number || '',
    venue: reservationData.venue || '',
    paid_in_full: !!reservationData.paid_in_full,
    payment_comment: reservationData.payment_comment || '',
    correction_notes: reservationData.correction_notes || '',
    heel_height: reservationData.heel_height || '',
    dress_length: reservationData.dress_length || '',
    corset_notes: reservationData.corset_notes || '',
    strap_notes: reservationData.strap_notes || '',
    neckline_notes: reservationData.neckline_notes || '',
    side_slit_notes: reservationData.side_slit_notes || '',
    general_notes: reservationData.general_notes || '',
    veil_size: reservationData.veil_size || '',
    glove_size: reservationData.glove_size || '',
    hair_flower_count: reservationData.hair_flower_count || 0,
    handbag_id: reservationData.handbag_id || '',
    coat_id: reservationData.coat_id || '',
    wrap_id: reservationData.wrap_id || '',
    bolero_id: reservationData.bolero_id || '',
    tiara_id: reservationData.tiara_id || '',
    sash_id: reservationData.sash_id || '',
    fur_jacket_id: reservationData.fur_jacket_id || '',
    veil_id: reservationData.veil_id || '',
    protective_bag_count: reservationData.protective_bag_count || 0,
    small_protective_bag_count: reservationData.small_protective_bag_count || 0,
    hanger_hook_count: reservationData.hanger_hook_count || 0,
    crinoline_size: reservationData.crinoline_size || 0
  });

  // Scroll to first error field when form is submitted with errors
  useEffect(() => {
    if (shouldScrollToFirstErrorRef.current && Object.keys(errors).length > 0 && modalBodyRef.current) {
      // Order of fields in the form (to find first error)
      const fieldOrder = ['dress_search', 'transaction_type', 'reservation_date', 'wedding_date', 'contract_date', 'bride_name', 'bride_surname', 'price'];
      
      // Find the first field with an error
      const firstErrorField = fieldOrder.find(field => errors[field]);
      
      if (firstErrorField) {
        // Small delay to ensure DOM is updated
        setTimeout(() => {
          const errorElement = document.getElementById(firstErrorField);
          if (errorElement) {
            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Focus the field to make it even more obvious
            errorElement.focus();
          }
          // Reset the flag after scrolling
          shouldScrollToFirstErrorRef.current = false;
        }, 100);
      } else {
        // If no specific field error found, reset flag
        shouldScrollToFirstErrorRef.current = false;
      }
    } else if (error && modalBodyRef.current && !shouldScrollToFirstErrorRef.current) {
      // If there's a general error (not field-specific), scroll to top
      modalBodyRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [error, errors]);

  // Function to get default form data
  const getDefaultFormData = useCallback(() => {
    const today = new Date().toISOString().split('T')[0];
    const defaultDeposit = salonSettings.default_deposit?.value || 0;
    
    return {
      dress_id: '',
      reservation_date: today,
      wedding_date: '',
      contract_date: today,
      transaction_type: 'rental',
      measurement_datetime: '',
      dress_pickup_datetime: '',
      dress_return_datetime: '',
      bride_name: '',
      bride_surname: '',
      bride_telephone: '',
      bride_city: '',
      bride_address: '',
      bride_id_card_number: '',
      bride_id_card_city: '',
      bride_personal_id_number: '',
      bride_instagram: '',
      bride_birth_year: '',
      price: '',
      deposit: defaultDeposit,
      voucher_number: '',
      venue: '',
      paid_in_full: false,
      payment_comment: '',
      correction_notes: '',
      heel_height: '',
      dress_length: '',
      corset_notes: '',
      strap_notes: '',
      neckline_notes: '',
      side_slit_notes: '',
      general_notes: '',
      veil_size: '',
      glove_size: '',
      hair_flower_count: 0,
      handbag_id: '',
      coat_id: '',
      wrap_id: '',
      bolero_id: '',
      tiara_id: '',
      sash_id: '',
      fur_jacket_id: '',
      veil_id: '',
      protective_bag_count: 1,
      small_protective_bag_count: 1,
      hanger_hook_count: 1,
      crinoline_size: 0
    };
  }, [salonSettings]);

  const fetchAccessories = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      // Fetch all accessories without pagination
      const response = await axios.get(`/api/salons/${salonId}/accessories?all=true`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const accessoriesData = response.data.accessories || [];
      setAccessories(accessoriesData);
    } catch (error) {
      console.error('Error fetching accessories:', error);
      setError(t('messages.error.failedToFetchAccessories'));
    }
  }, [salonId, t]);

  const fetchReferralSources = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/salons/${salonId}/referral-sources?limit=1000`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const sourcesData = response.data.sources || [];
      // Filter only active sources
      const activeSources = sourcesData.filter(source => source.is_active);
      setReferralSources(activeSources);
    } catch (error) {
      console.error('Error fetching referral sources:', error);
      // Don't show error for referral sources as it's not critical
    }
  }, [salonId]);

  const fetchContracts = useCallback(async (transactionType) => {
    if (!transactionType) {
      setAvailableContracts([]);
      setSelectedContractId('');
      return;
    }

    try {
      setIsLoadingContracts(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/salons/${salonId}/contracts?type=${transactionType}&limit=1000`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const contracts = response.data.contracts || [];
      setAvailableContracts(contracts);
      
      // Auto-select active contract if available, otherwise select first contract
      const activeContract = contracts.find(c => c.is_active);
      if (activeContract) {
        setSelectedContractId(activeContract.id);
      } else if (contracts.length > 0) {
        setSelectedContractId(contracts[0].id);
      } else {
        setSelectedContractId('');
      }
    } catch (error) {
      console.error('Error fetching contracts:', error);
      setAvailableContracts([]);
      setSelectedContractId('');
    } finally {
      setIsLoadingContracts(false);
    }
  }, [salonId]);

  const fetchDressDetails = useCallback(async (dressId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/salons/${salonId}/dresses/${dressId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data.dress;
    } catch (error) {
      console.error('Error fetching dress details:', error);
      return null;
    }
  }, [salonId]);

  useEffect(() => {
    if (isOpen) {
      fetchAccessories();
      fetchReferralSources();
      
      // Reset change log visibility when modal opens
      setIsChangeLogVisible(false);
      
      if (mode === 'edit' && reservation) {
        const mappedData = formatReservationForForm(reservation);
        setFormData(mappedData);
        originalReservationRef.current = mappedData;
        
        // Fetch contracts for the transaction type
        if (mappedData.transaction_type) {
          fetchContracts(mappedData.transaction_type);
        }
        
        // Parse referral sources from the referral_sources array
        if (reservation.referral_sources && Array.isArray(reservation.referral_sources)) {
          const sourceNames = reservation.referral_sources.map(rs => rs.source_name);
          setSelectedReferralSources(sourceNames);
        } else {
          setSelectedReferralSources([]);
        }
        
        // Set selected dress if available - fetch full dress details including images
        if (reservation.dress_id && reservation.dress_name && reservation.brand_name) {
          // First set basic dress info
          setSelectedDress({
            id: reservation.dress_id,
            name: reservation.dress_name,
            brand_name: reservation.brand_name,
            images: [] // Will be populated after fetch
          });
          
          // Fetch full dress details including images and stock count
          fetchDressDetails(reservation.dress_id).then(dressDetails => {
            if (dressDetails) {
              setSelectedDress({
                id: dressDetails.id,
                name: dressDetails.name,
                brand_name: dressDetails.brand_name,
                images: dressDetails.images || [],
                stock_count: dressDetails.stock_count
              });
            }
          });
        }
      } else {
        // Reset form for add mode - use default values
        setFormData(getDefaultFormData());
        setSelectedReferralSources([]);
        setAvailableContracts([]);
        setSelectedContractId('');
      }
      
      // Reset search states
      setDressSearchTerm('');
      setDressSearchResults([]);
      if (mode !== 'edit') {
        setSelectedDress(null);
      }
      setShowSearchResults(false);
      setShowReferralDropdown(false);
      isSelectingDress.current = false;
      
      // Clear errors when modal opens
      setError('');
      setErrors({});
      // Reset scroll flag when modal opens
      shouldScrollToFirstErrorRef.current = false;
    }
  }, [isOpen, mode, reservation, salonId, fetchAccessories, fetchReferralSources, fetchDressDetails, getDefaultFormData, fetchContracts]);

  // Reset form when modal is closed
  useEffect(() => {
    if (!isOpen) {
      setFormData(getDefaultFormData());
      setSelectedDress(null);
      setDressSearchTerm('');
      setDressSearchResults([]);
      setShowSearchResults(false);
      setError('');
      isSelectingDress.current = false;
    }
  }, [isOpen, getDefaultFormData]);

  // Clear search results when a dress is selected
  useEffect(() => {
    console.log('useEffect triggered - selectedDress:', selectedDress);
    if (selectedDress) {
      console.log('Clearing search results due to selectedDress');
      setDressSearchResults([]);
    }
  }, [selectedDress]);

  // Update deposit when settings are loaded (add mode only)
  useEffect(() => {
    if (isOpen && mode === 'add' && salonSettings.default_deposit?.value !== undefined) {
      const defaultDeposit = salonSettings.default_deposit.value;
      setFormData(prev => ({
        ...prev,
        deposit: defaultDeposit
      }));
    }
  }, [salonSettings, isOpen, mode]);

  // Handle prefill data from appointment navigation
  useEffect(() => {
    if (isOpen && prefillData && mode === 'add') {
      console.log('Prefilling reservation data:', prefillData);
      setFormData(prevData => ({
        ...prevData,
        bride_name: prefillData.bride_name || '',
        bride_surname: prefillData.bride_surname || '',
        wedding_date: prefillData.wedding_date || '',
        dress_pickup_datetime: prefillData.dress_pickup_datetime || '',
        dress_return_datetime: prefillData.dress_return_datetime || '',
        bride_telephone: prefillData.phone_number || '',
        bride_instagram: prefillData.instagram_handle || '',
        reservation_date: prefillData.reservation_date || '',
        contract_date: prefillData.contract_date || ''
      }));
      // Capture originating appointment id (used to link the new reservation back to the appointment server-side)
      const apptId = prefillData.appointment_id != null && prefillData.appointment_id !== ''
        ? parseInt(String(prefillData.appointment_id), 10)
        : NaN;
      originatingAppointmentIdRef.current = Number.isNaN(apptId) ? null : apptId;
      // Clear the prefill data after using it
      delete window.reservationPrefillData;
    }
    if (isOpen && (!prefillData || mode !== 'add')) {
      originatingAppointmentIdRef.current = null;
    }
  }, [isOpen, prefillData, mode]);


  // Helper function to get accessories by type
  const getAccessoriesByType = (type) => {
    return accessories.filter(accessory => accessory.type === type);
  };

  // Handle clicking on accessory image
  const handleAccessoryImageClick = (accessory) => {
    if (accessory.image) {
      setViewerImages([accessory.image]);
      setCurrentImageIndex(0);
      setShowImageViewer(true);
    }
  };

  // Handle clicking on dress image
  const handleDressImageClick = (dress) => {
    if (dress.images && dress.images.length > 0) {
      // Convert relative paths to full URLs
      const fullImageUrls = dress.images.map(imagePath => getImageUrl(imagePath));
      
      // Add video as the last item if it exists
      if (dress.video_url) {
        const videoUrl = getImageUrl(dress.video_url);
        fullImageUrls.push({
          type: 'video',
          url: videoUrl,
          thumbnail: videoUrl
        });
      }
      
      // Add second video if it exists
      if (dress.video2_url) {
        const video2Url = getImageUrl(dress.video2_url);
        fullImageUrls.push({
          type: 'video',
          url: video2Url,
          thumbnail: video2Url
        });
      }
      
      setViewerImages(fullImageUrls);
      setCurrentImageIndex(0);
      setShowImageViewer(true);
    }
  };

  // Memoized selected accessories for preview
  const selectedAccessories = useMemo(() => {
    // Don't calculate if accessories haven't been loaded yet
    if (accessories.length === 0) {
      return [];
    }
    
    return [
      { field: 'handbag_id', type: 'handbag', labelKey: 'reservations.handbag' },
      { field: 'coat_id', type: 'coat', labelKey: 'reservations.coat' },
      { field: 'wrap_id', type: 'wrap', labelKey: 'reservations.wrap' },
      { field: 'bolero_id', type: 'bolero', labelKey: 'reservations.bolero' },
      { field: 'tiara_id', type: 'tiara', labelKey: 'reservations.tiara' },
      { field: 'sash_id', type: 'sash', labelKey: 'reservations.sash' },
      { field: 'fur_jacket_id', type: 'fur_jacket', labelKey: 'reservations.furJacket' },
      { field: 'veil_id', type: 'veil', labelKey: 'reservations.veil' }
    ].map(({ field, type, labelKey }) => {
      const accessoryId = formData[field];
      // Convert accessoryId to number for comparison since database IDs are numeric
      const numericAccessoryId = accessoryId ? parseInt(accessoryId) : null;
      const accessory = numericAccessoryId ? accessories.find(acc => acc.id === numericAccessoryId) : null;
      return accessory ? { ...accessory, field, type, labelKey } : null;
    }).filter(Boolean);
  }, [formData, accessories]);



  const searchDresses = async (searchTerm) => {
    if (searchTerm.trim().length < 2) {
      setDressSearchResults([]);
      return;
    }

    setIsSearchingDresses(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/salons/${salonId}/dresses?search=${encodeURIComponent(searchTerm)}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setDressSearchResults(response.data.dresses || []);
    } catch (error) {
      console.error('Error searching dresses:', error);
      setDressSearchResults([]);
    } finally {
      setIsSearchingDresses(false);
    }
  };

  const handleDressSearchChange = (e) => {
    const value = e.target.value;
    setDressSearchTerm(value);
    
    // Only clear selected dress if we're not in the process of selecting one
    if (!isSelectingDress.current) {
      setSelectedDress(null);
      setFormData(prev => ({
        ...prev,
        dress_id: ''
      }));
    }

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Search after user stops typing for 300ms
    if (value.trim().length >= 2) {
      setShowSearchResults(true);
      searchTimeoutRef.current = setTimeout(() => {
        searchDresses(value);
      }, 300);
    } else {
      setDressSearchResults([]);
      setShowSearchResults(false);
    }
  };

  const handleDressSelect = (dress) => {
    console.log('Dress selected:', dress);
    console.log('Before selection - searchResults:', dressSearchResults.length, 'selectedDress:', selectedDress);
    
    // Reset scroll flag when user selects a dress (they're fixing errors)
    shouldScrollToFirstErrorRef.current = false;
    
    // Clear error for dress selection when user selects a dress
    if (errors.dress_search) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.dress_search;
        return newErrors;
      });
    }
    
    // Clear any pending search timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    
    // Set flag to prevent clearing selected dress
    isSelectingDress.current = true;
    
    // Hide search results immediately
    setShowSearchResults(false);
    setDressSearchResults([]);
    
    setSelectedDress(dress);
    setDressSearchTerm(dress.name);
    
    // Auto-fill price based on transaction type
    const price = formData.transaction_type === 'rental' ? dress.rental_price : dress.selling_price;
    const hasAutoPrice = price !== undefined && price !== null && price !== '';
    
    setFormData(prev => ({
      ...prev,
      dress_id: dress.id,
      price: hasAutoPrice ? price : prev.price
    }));
    
    console.log('After selection - should clear search results');
    
    // Reset flag after a short delay
    setTimeout(() => {
      isSelectingDress.current = false;
      console.log('Flag reset, isSelectingDress:', isSelectingDress.current);
    }, 100);
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    const processedValue = type === 'checkbox' ? e.target.checked : value;
    
    // Reset scroll flag when user starts typing (they're fixing errors)
    shouldScrollToFirstErrorRef.current = false;
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    
    // Handle foreign key fields specially
    const foreignKeyFields = ['handbag_id', 'coat_id', 'wrap_id', 'bolero_id', 'tiara_id', 'sash_id', 'fur_jacket_id', 'veil_id'];
    let finalValue = processedValue;
    
    if (foreignKeyFields.includes(name)) {
      // Convert empty string to empty string for foreign keys (don't convert to 0)
      finalValue = value === '' ? '' : value;
    } else if (type === 'number') {
      finalValue = parseInt(value) || 0;
    }
    
    // Handle transaction type change - clear return datetime for sale
    if (name === 'transaction_type') {
      setFormData(prev => {
        const updated = {
          ...prev,
          [name]: finalValue
        };
        
        // If changing to sale, clear return datetime
        if (finalValue === 'sale') {
          updated.dress_return_datetime = '';
        }
        
        // Auto-update price when transaction type changes and dress is selected
        if (selectedDress) {
          const price = finalValue === 'rental' ? selectedDress.rental_price : selectedDress.selling_price;
          if (price !== undefined && price !== null && price !== '') {
            updated.price = price;
          }
        }
        
        return updated;
      });
      
      // Fetch contracts for the new transaction type (only in edit mode)
      if (mode === 'edit') {
        fetchContracts(finalValue);
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: finalValue
      }));
    }

    // Auto-calculate pickup and return dates when wedding date changes
    if (name === 'wedding_date' && value) {
      const weddingDate = new Date(value);
      
      // Validate that the wedding date is valid
      if (isNaN(weddingDate.getTime())) {
        return;
      }
      
      const pickupDate = new Date(weddingDate);
      const returnDate = new Date(weddingDate);
      
      // Pickup: 2 days before wedding at 15:00
      pickupDate.setDate(pickupDate.getDate() - 2);
      pickupDate.setHours(13, 0, 0, 0);
      
      // Format dates for datetime-local input (YYYY-MM-DDTHH:mm)
      const formatDateTime = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
      };
      
      // Only calculate return date for rental transactions
      setFormData(prev => {
        const updates = {
          ...prev,
          dress_pickup_datetime: formatDateTime(pickupDate)
        };
        
        // Only set return datetime if transaction type is rental
        if (prev.transaction_type === 'rental') {
          // Return: 2 days after wedding at 15:00
          returnDate.setDate(returnDate.getDate() + 2);
          returnDate.setHours(15, 0, 0, 0);
          
          // If return date is Sunday, move to Monday
          if (returnDate.getDay() === 0) {
            returnDate.setDate(returnDate.getDate() + 1);
          }
          
          updates.dress_return_datetime = formatDateTime(returnDate);
        }
        
        return updates;
      });
    }
  };

  const handleReferralSourceToggle = (sourceName) => {
    setSelectedReferralSources(prev => {
      if (prev.includes(sourceName)) {
        return prev.filter(s => s !== sourceName);
      } else {
        return [...prev, sourceName];
      }
    });
  };

  const handleReferralDropdownToggle = () => {
    setShowReferralDropdown(!showReferralDropdown);
  };

  // Close dropdown when clicking outside
  const referralDropdownRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (referralDropdownRef.current && !referralDropdownRef.current.contains(event.target)) {
        setShowReferralDropdown(false);
      }
    };
    
    if (showReferralDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showReferralDropdown]);


  const logReservationChanges = async (originalReservation, updatedData) => {
    if (!originalReservation || !reservation?.id) {
      console.log('logReservationChanges: Skipping - no originalReservation or reservation.id');
      return;
    }

    console.log('logReservationChanges: Starting', { 
      reservationId: reservation.id,
      originalKeys: Object.keys(originalReservation),
      updatedKeys: Object.keys(updatedData)
    });

    const trackedFields = [
      'dress_id',
      'reservation_date',
      'wedding_date',
      'contract_date',
      'transaction_type',
      'measurement_datetime',
      'dress_pickup_datetime',
      'dress_return_datetime',
      'bride_name',
      'bride_surname',
      'bride_telephone',
      'bride_city',
      'bride_address',
      'bride_id_card_number',
      'bride_id_card_city',
      'bride_personal_id_number',
      'bride_instagram',
      'bride_birth_year',
      'price',
      'deposit',
      'voucher_number',
      'venue',
      'paid_in_full',
      'payment_comment',
      'correction_notes',
      'heel_height',
      'dress_length',
      'corset_notes',
      'strap_notes',
      'neckline_notes',
      'side_slit_notes',
      'general_notes',
      'veil_size',
      'glove_size',
      'hair_flower_count',
      'handbag_id',
      'coat_id',
      'wrap_id',
      'bolero_id',
      'tiara_id',
      'sash_id',
      'fur_jacket_id',
      'veil_id',
      'protective_bag_count',
      'small_protective_bag_count',
      'hanger_hook_count',
      'crinoline_size'
    ];

    const sanitizeValue = (value) => {
      if (value === null || value === undefined || value === '') return '';
      // Convert numbers to strings for comparison
      if (typeof value === 'number') return String(value);
      return String(value).trim();
    };

    const changes = trackedFields.reduce((acc, field) => {
      const previousValue = sanitizeValue(originalReservation[field]);
      const newValue = sanitizeValue(updatedData[field]);
      if (previousValue !== newValue) {
        acc.push({
          field_name: field,
          previous_value: previousValue || null,
          new_value: newValue || null
        });
      }
      return acc;
    }, []);

    console.log('logReservationChanges: Found changes', changes);

    if (changes.length === 0) {
      console.log('logReservationChanges: No changes detected');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      console.error('logReservationChanges: No token found');
      return;
    }

    try {
      const results = await Promise.all(
        changes.map(change =>
          axios.post(
            `/api/salons/${salonId}/change-logs`,
            {
              entity_type: 'reservation',
              entity_id: reservation.id,
              field_name: change.field_name,
              previous_value: change.previous_value,
              new_value: change.new_value,
              change_type: 'update',
              metadata: {
                reservation_id: reservation.id
              }
            },
            {
              headers: {
                Authorization: `Bearer ${token}`
              }
            }
          ).then(response => {
            console.log('logReservationChanges: Successfully logged change', {
              field: change.field_name,
              response: response.data
            });
            return response;
          }).catch(error => {
            console.error('Error logging reservation change:', {
              field: change.field_name,
              error: error.response?.data || error.message,
              status: error.response?.status
            });
            throw error;
          })
        )
      );
      console.log('logReservationChanges: All changes logged successfully', results);
    } catch (error) {
      console.error('logReservationChanges: Error logging changes', error);
    }

    originalReservationRef.current = {
      ...originalReservation,
      ...updatedData
    };
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!selectedDress) {
      newErrors.dress_search = t('messages.error.dressSelectionRequired');
    }
    
    if (!formData.transaction_type) {
      newErrors.transaction_type = t('messages.error.transactionTypeRequired');
    }
    
    if (!formData.reservation_date) {
      newErrors.reservation_date = t('messages.error.reservationDateRequired');
    }
    
    if (!formData.wedding_date) {
      newErrors.wedding_date = t('messages.error.weddingDateRequired');
    }
    
    if (!formData.contract_date) {
      newErrors.contract_date = t('messages.error.contractDateRequired');
    }
    
    if (!formData.bride_name || !formData.bride_name.trim()) {
      newErrors.bride_name = t('messages.error.brideNameRequired');
    }
    
    if (!formData.bride_surname || !formData.bride_surname.trim()) {
      newErrors.bride_surname = t('messages.error.brideSurnameRequired');
    }
    
    if (!formData.price || formData.price <= 0) {
      newErrors.price = t('messages.error.validPriceRequired');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!validateForm()) {
      // Set flag to scroll to first error on next render
      shouldScrollToFirstErrorRef.current = true;
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      setErrors({});
      
      const token = localStorage.getItem('token');
      const url = mode === 'edit' 
        ? `/api/salons/${salonId}/reservations/${reservation.id}`
        : `/api/salons/${salonId}/reservations`;
      
      const method = mode === 'edit' ? 'put' : 'post';
      
      // Convert selected referral source names to IDs
      const referral_source_ids = selectedReferralSources
        .map(sourceName => {
          const source = referralSources.find(rs => rs.source_name === sourceName);
          return source ? source.id : null;
        })
        .filter(id => id !== null);
      
      const submitData = {
        ...formData,
        referral_source_ids
      };
      // When creating a reservation from an appointment, include the originating appointment id
      // so the server links the new reservation back to that appointment.
      if (mode === 'add' && originatingAppointmentIdRef.current) {
        submitData.appointment_id = originatingAppointmentIdRef.current;
      }
      
      const response = await axios[method](url, submitData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 200 || response.status === 201) {
        if (mode === 'edit') {
          await logReservationChanges(
            originalReservationRef.current || formatReservationForForm(reservation),
            formData
          );
        }
        onReservationAdded();
        handleClose();
        // Reset form
        setFormData(getDefaultFormData());
        setSelectedReferralSources([]);
        // Reset search states
        setDressSearchTerm('');
        setDressSearchResults([]);
        setSelectedDress(null);
        setShowSearchResults(false);
        isSelectingDress.current = false;
      }
    } catch (error) {
      console.error('Error creating reservation:', error);
      setError(error.response?.data?.error || t('messages.error.failedToCreateReservation'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = () => {
    if (window.confirm('Are you sure you want to delete this reservation? This action cannot be undone.')) {
      handleDeleteReservation();
    }
  };

  const handleDeleteReservation = async () => {
    if (!reservation || !reservation.id) {
      setError('No reservation selected for deletion');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      
      const token = localStorage.getItem('token');
      const response = await axios.delete(`/api/salons/${salonId}/reservations/${reservation.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 200) {
        onReservationAdded(); // Refresh the reservations list
        handleClose();
      }
    } catch (error) {
      console.error('Error deleting reservation:', error);
      setError(error.response?.data?.error || t('messages.error.failedToDeleteReservation'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleChangeLog = () => {
    if (!reservation?.id) return;
    setIsChangeLogVisible(!isChangeLogVisible);
  };

  // Handle close - clear address fields to prevent Chrome from saving address
  const handleClose = () => {
    // Clear address-related fields in the DOM before closing to prevent Chrome from detecting and saving them
    const addressFields = ['bride_name', 'bride_surname', 'bride_telephone', 'bride_city', 'bride_address'];
    addressFields.forEach(field => {
      const input = document.getElementById(field);
      if (input) {
        // Clear the value and trigger change event to update React state
        input.value = '';
        // Also clear from React state
        setFormData(prev => ({
          ...prev,
          [field]: ''
        }));
      }
    });
    
    // Use requestAnimationFrame to ensure DOM is updated before Chrome checks
    requestAnimationFrame(() => {
      // Small additional delay to ensure Chrome doesn't detect the fields
      setTimeout(() => {
        onClose();
      }, 50);
    });
  };

  const handlePrintContract = async () => {
    if (!formData.transaction_type) {
      setContractError(t('messages.error.selectTransactionTypeFirst'));
      return;
    }

    if (!selectedContractId) {
      setContractError(t('messages.error.selectContractFirst'));
      return;
    }

    try {
      setIsLoadingContract(true);
      setContractError('');
      
      const token = localStorage.getItem('token');

      // Fetch the full contract details including the complete content
      const contractResponse = await axios.get(`/api/salons/${salonId}/contracts/${selectedContractId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const contract = contractResponse.data.contract || contractResponse.data;
      
      // Check if contract_content is empty but preview has content
      if ((!contract.contract_content || contract.contract_content.length === 0) && contract.preview && contract.preview.length > 0) {
        contract.contract_content = contract.preview;
      }
      
      // Final check for content
      if (!contract.contract_content || contract.contract_content.length === 0) {
        setContractError(t('messages.error.contractTemplateNoContent'));
        return;
      }
      
      // Generate PDF with filled placeholders
      await generateContractPDF(contract);
      
    } catch (error) {
      console.error('Error fetching contract:', error);
      setContractError(t('messages.error.failedToFetchContractTemplate', '', { message: error.message || t('messages.error.generic') }));
    } finally {
      setIsLoadingContract(false);
    }
  };

  const generateContractPDF = async (contract) => {
    try {
      // Replace placeholders with form data
      let contractContent = contract.contract_content || '';
      
      // Check if contract content exists
      if (!contractContent) {
        throw new Error(t('messages.error.contractTemplateNoContent'));
      }
      
      // Helper function to format date as "14 Jun 2026"
      const formatDateForPDF = (dateString) => {
        if (!dateString) return '';
        try {
          const date = new Date(dateString);
          if (isNaN(date.getTime())) return dateString; // Return original if invalid
          const day = date.getDate();
          const month = getMonthName(date.getMonth(), true); // Short month name
          const year = date.getFullYear();
          return `${day} ${month} ${year}`;
        } catch (error) {
          return dateString; // Return original if parsing fails
        }
      };
      
      // Helper function to format datetime as "14 Jun 2026 14:00"
      const formatDateTimeForPDF = (datetimeString) => {
        if (!datetimeString) return '';
        try {
          const date = new Date(datetimeString);
          if (isNaN(date.getTime())) return datetimeString; // Return original if invalid
          const day = date.getDate();
          const month = getMonthName(date.getMonth(), true); // Short month name
          const year = date.getFullYear();
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          return `${day} ${month} ${year} ${hours}:${minutes}`;
        } catch (error) {
          return datetimeString; // Return original if parsing fails
        }
      };
      
      // Define placeholder mappings
      const placeholders = {
        // Dress information
        '{{dress_name}}': selectedDress?.name || '',
        '{{dress_brand_name}}': selectedDress?.brand_name || '',
        
        // Bride information
        '{{bride_name}}': formData.bride_name || '',
        '{{bride_surname}}': formData.bride_surname || '',
        '{{bride_telephone}}': formData.bride_telephone || '',
        '{{bride_city}}': formData.bride_city || '',
        '{{bride_address}}': formData.bride_address || '',
        '{{bride_id_card_number}}': formData.bride_id_card_number || '',
        '{{bride_id_card_city}}': formData.bride_id_card_city || '',
        '{{bride_personal_id_number}}': formData.bride_personal_id_number || '',
        '{{bride_instagram}}': formData.bride_instagram || '',
        '{{bride_birth_year}}': formData.bride_birth_year || '',
        
        // Date information - formatted to match frontend format
        '{{wedding_date}}': formatDateForPDF(formData.wedding_date),
        '{{reservation_date}}': formatDateForPDF(formData.reservation_date),
        '{{contract_date}}': formatDateForPDF(formData.contract_date),
        '{{dress_pickup_datetime}}': formatDateTimeForPDF(formData.dress_pickup_datetime),
        '{{dress_return_datetime}}': formatDateTimeForPDF(formData.dress_return_datetime),
        '{{measurement_datetime}}': formatDateTimeForPDF(formData.measurement_datetime),
        
        // Financial information
        '{{price}}': formData.price || '',
        '{{deposit}}': formData.deposit || '',
        '{{voucher_number}}': formData.voucher_number || '',
        '{{referral_sources}}': selectedReferralSources.join(', ') || '',
        '{{venue}}': formData.venue || '',
        
        // Accessories and measurements
        '{{veil_size}}': formData.veil_size || '',
        '{{glove_size}}': formData.glove_size || '',
        '{{hair_flower_count}}': formData.hair_flower_count || '0',
        '{{protective_bag_count}}': formData.protective_bag_count || '0',
        '{{small_protective_bag_count}}': formData.small_protective_bag_count || '0',
        '{{hanger_hook_count}}': formData.hanger_hook_count || '0',
        '{{crinoline_size}}': formData.crinoline_size || '0',
        
        // Accessory names (from selected accessories)
        '{{handbag_name}}': selectedAccessories.find(acc => acc.type === 'handbag')?.name || '',
        '{{coat_name}}': selectedAccessories.find(acc => acc.type === 'coat')?.name || '',
        '{{wrap_name}}': selectedAccessories.find(acc => acc.type === 'wrap')?.name || '',
        '{{bolero_name}}': selectedAccessories.find(acc => acc.type === 'bolero')?.name || '',
        '{{tiara_name}}': selectedAccessories.find(acc => acc.type === 'tiara')?.name || '',
        '{{sash_name}}': selectedAccessories.find(acc => acc.type === 'sash')?.name || '',
        '{{fur_jacket_name}}': selectedAccessories.find(acc => acc.type === 'fur_jacket')?.name || '',
        '{{veil_name}}': selectedAccessories.find(acc => acc.type === 'veil')?.name || ''
      };

      printTemplateDocument({
        title: contract.title || 'Contract',
        templateContent: contractContent,
        placeholderMap: placeholders,
        contentClass: 'contract-content'
      });
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      setContractError(t('messages.error.failedToGeneratePDF', '', { message: error.message || t('messages.error.generic') }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h3>{mode === 'edit' ? t('reservations.reservation') : t('reservations.addReservation')}</h3>
          <button className="modal-close" onClick={handleClose}>×</button>
        </div>
        
        <div className="modal-body" ref={modalBodyRef}>
          <form onSubmit={handleSubmit} className="base-form" autoComplete="off" noValidate>
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}
            
            {contractError && (
              <div className="error-message">
                {contractError}
              </div>
            )}

            {isEditLocked && (
              <div className="info-banner" style={{ background: 'var(--warning-bg, #fff3cd)', color: 'var(--warning-text, #856404)', padding: '12px 16px', borderRadius: '8px', marginBottom: '16px', border: '1px solid var(--warning-border, #ffc107)' }}>
                {t('reservations.editLockedMessage')}
              </div>
            )}

            <fieldset disabled={isEditLocked} style={{ border: 'none', padding: 0, margin: 0 }}>

            {/* Basic Information */}
            <div className="form-section">
              <h4>{t('reservations.basicInformation')}</h4>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="dress_search">{t('reservations.dress')} *</label>
                  <div className="email-search-container">
                    <input
                      type="text"
                      id="dress_search"
                      value={dressSearchTerm}
                      onChange={handleDressSearchChange}
                      placeholder={t('reservations.searchDress')}
                      className={`email-input ${errors.dress_search ? 'error' : ''}`}
                      autoComplete="off"
                      required
                    />
                    {isSearchingDresses && (
                      <div className="search-spinner">
                        <div className="spinner"></div>
                      </div>
                    )}
                  </div>

                  {/* Search Results Dropdown */}
                  {console.log('Render check:', { 
                    searchResultsLength: dressSearchResults.length, 
                    selectedDress: selectedDress,
                    showSearchResults: showSearchResults,
                    shouldShow: showSearchResults && dressSearchResults.length > 0 && !selectedDress 
                  })}
                  {showSearchResults && dressSearchResults.length > 0 && !selectedDress && (
                    <div className="search-results">
                      {dressSearchResults.map((dress) => (
                        <div
                          key={dress.id}
                          className="search-result-item"
                          onClick={() => handleDressSelect(dress)}
                        >
                          <DressDisplay 
                            dress={dress}
                            imageClassName="dress-thumbnail"
                            showStockCount={dress.stock_count === 0}
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Selected Dress Display */}
                  {selectedDress && (
                    <div className="selected-user">
                      <DressDisplay 
                        dress={selectedDress}
                        onImageClick={handleDressImageClick}
                        imageClassName="dress-thumbnail"
                        showVideo={false}
                        showStockCount={true}
                      />
                      <button
                        className="change-user-btn"
                        onClick={() => {
                          setSelectedDress(null);
                          setDressSearchTerm('');
                        setShowSearchResults(false);
                        isSelectingDress.current = false;
                      }}
                    >
                      {t('common.change')}
                    </button>
                  </div>
                )}
                {errors.dress_search && <span className="error-message">{errors.dress_search}</span>}
                </div>
                <div className="form-group">
                  <label htmlFor="transaction_type">{t('reservations.transactionType')} *</label>
                  <select
                    id="transaction_type"
                    name="transaction_type"
                    value={formData.transaction_type}
                    onChange={handleInputChange}
                    className={errors.transaction_type ? 'error' : ''}
                    required
                  >
                    <option value="rental">{t('reservations.rental')}</option>
                    <option value="sale">{t('reservations.sale')}</option>
                  </select>
                  {errors.transaction_type && <span className="error-message">{errors.transaction_type}</span>}
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="reservation_date">{t('reservations.reservationDate')} *</label>
                  <div className="date-field-wrap">
                    <input
                      type="date"
                      id="reservation_date"
                      name="reservation_date"
                      value={formData.reservation_date}
                      onChange={handleInputChange}
                      className={errors.reservation_date ? 'error' : ''}
                      required
                    />
                  </div>
                  {errors.reservation_date && <span className="error-message">{errors.reservation_date}</span>}
                </div>
                <div className="form-group">
                  <label htmlFor="wedding_date">{t('reservations.weddingDate')} *</label>
                  <div className="date-field-wrap">
                    <input
                      type="date"
                      id="wedding_date"
                      name="wedding_date"
                      value={formData.wedding_date}
                      onChange={handleInputChange}
                      className={errors.wedding_date ? 'error' : ''}
                      required
                    />
                  </div>
                  {errors.wedding_date && <span className="error-message">{errors.wedding_date}</span>}
                </div>
                <div className="form-group">
                  <label htmlFor="contract_date">{t('reservations.contractDate')} *</label>
                  <div className="date-field-wrap">
                    <input
                      type="date"
                      id="contract_date"
                      name="contract_date"
                      value={formData.contract_date}
                      onChange={handleInputChange}
                      className={errors.contract_date ? 'error' : ''}
                      required
                    />
                  </div>
                  {errors.contract_date && <span className="error-message">{errors.contract_date}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="measurement_datetime">{t('reservations.measurementDateTime')}</label>
                  <div className="date-field-wrap">
                    <input
                      type="datetime-local"
                      id="measurement_datetime"
                      name="measurement_datetime"
                      value={formData.measurement_datetime}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="dress_pickup_datetime">
                    {t('reservations.pickupDateTime')}
                    <span 
                      className="tooltip-icon" 
                      title={t('reservations.pickupTooltip')}
                    >
                      ?
                    </span>
                  </label>
                  <div className="date-field-wrap">
                    <input
                      type="datetime-local"
                      id="dress_pickup_datetime"
                      name="dress_pickup_datetime"
                      value={formData.dress_pickup_datetime}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="dress_return_datetime">
                    {t('reservations.returnDateTime')}
                    <span 
                      className="tooltip-icon" 
                      title={t('reservations.returnTooltip')}
                    >
                      ?
                    </span>
                  </label>
                  <div className="date-field-wrap">
                    <input
                      type="datetime-local"
                      id="dress_return_datetime"
                      name="dress_return_datetime"
                      value={formData.dress_return_datetime}
                      onChange={handleInputChange}
                      disabled={formData.transaction_type === 'sale'}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Bride Information */}
            <div className="form-section">
              <h4>{t('reservations.brideInformation')}</h4>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="bride_name">{t('reservations.brideName')} *</label>
                  <input
                    type="text"
                    id="bride_name"
                    name="bride_name"
                    value={formData.bride_name}
                    onChange={handleInputChange}
                    className={errors.bride_name ? 'error' : ''}
                    required
                    autoComplete="off"
                  />
                  {errors.bride_name && <span className="error-message">{errors.bride_name}</span>}
                </div>
                <div className="form-group">
                  <label htmlFor="bride_surname">{t('reservations.brideSurname')} *</label>
                  <input
                    type="text"
                    id="bride_surname"
                    name="bride_surname"
                    value={formData.bride_surname}
                    onChange={handleInputChange}
                    className={errors.bride_surname ? 'error' : ''}
                    required
                    autoComplete="off"
                  />
                  {errors.bride_surname && <span className="error-message">{errors.bride_surname}</span>}
                </div>
                <div className="form-group">
                  <label htmlFor="bride_city">{t('common.city')}</label>
                  <input
                    type="text"
                    id="bride_city"
                    name="bride_city"
                    value={formData.bride_city}
                    onChange={handleInputChange}
                    autoComplete="off"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="bride_birth_year">{t('reservations.brideBirthYear')}</label>
                  <input
                    type="number"
                    id="bride_birth_year"
                    name="bride_birth_year"
                    value={formData.bride_birth_year}
                    onChange={handleInputChange}
                    min="1900"
                    max={new Date().getFullYear()}
                    placeholder=""
                    autoComplete="off"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="bride_telephone">{t('common.phone')}</label>
                  <div className="input-with-icon">
                    <input
                      type="tel"
                      id="bride_telephone"
                      name="bride_telephone"
                      value={formData.bride_telephone}
                      onChange={handleInputChange}
                      autoComplete="off"
                    />
                    {formData.bride_telephone && (
                      <button
                        type="button"
                        className="contact-icon phone-icon"
                        onClick={() => window.open(`tel:${formData.bride_telephone}`, '_self')}
                        title={t('appointments.callBride')}
                      >
                        📞
                      </button>
                    )}
                    {formData.bride_telephone && (
                      <button
                        type="button"
                        className="contact-icon viber-icon"
                        onClick={() => {
                          const viberUrl = `viber://chat?number=${formData.bride_telephone}`;
                          window.open(viberUrl, '_self');
                        }}
                        title={t('appointments.openViberChat')}
                      >
                        <FontAwesomeIcon icon={faViber} />
                      </button>
                    )}
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="bride_instagram">{t('appointments.instagram')}</label>
                  <div className="input-with-icon">
                    <input
                      type="text"
                      id="bride_instagram"
                      name="bride_instagram"
                      value={formData.bride_instagram}
                      onChange={handleInputChange}
                      placeholder={t('appointments.instagramPlaceholder')}
                    />
                    {formData.bride_instagram && (
                      <button
                        type="button"
                        className="contact-icon instagram-icon"
                        onClick={() => {
                          const instagramUrl = formData.bride_instagram.startsWith('@') 
                            ? `https://instagram.com/${formData.bride_instagram.substring(1)}`
                            : `https://instagram.com/${formData.bride_instagram}`;
                          window.open(instagramUrl, '_blank');
                        }}
                        title={t('appointments.openInstagram')}
                      >
                        <FontAwesomeIcon icon={faInstagram} />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="bride_address">{t('common.address')}</label>
                  <textarea
                    id="bride_address"
                    name="bride_address"
                    value={formData.bride_address}
                    onChange={handleInputChange}
                    rows="2"
                    autoComplete="off"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="bride_id_card_number">{t('reservations.brideIdCardNumber')}</label>
                  <input
                    type="text"
                    id="bride_id_card_number"
                    name="bride_id_card_number"
                    value={formData.bride_id_card_number}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="bride_id_card_city">{t('reservations.brideIdCardCity')}</label>
                  <input
                    type="text"
                    id="bride_id_card_city"
                    name="bride_id_card_city"
                    value={formData.bride_id_card_city}
                    onChange={handleInputChange}
                    autoComplete="off"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="bride_personal_id_number">{t('reservations.bridePersonalIdNumber')}</label>
                  <input
                    type="text"
                    id="bride_personal_id_number"
                    name="bride_personal_id_number"
                    value={formData.bride_personal_id_number}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>

            {/* Financial Information */}
            <div className="form-section">
              <h4>{t('reservations.financialInformation')}</h4>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="price">{t('common.price')} ({currencySymbol}) *</label>
                  <input
                    type="number"
                    id="price"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    className={errors.price ? 'error' : ''}
                    step="0.01"
                    min="0"
                    required
                  />
                  {errors.price && <span className="error-message">{errors.price}</span>}
                </div>
                <div className="form-group">
                  <label htmlFor="deposit">{t('reservations.deposit')} ({currencySymbol})</label>
                  <input
                    type="number"
                    id="deposit"
                    name="deposit"
                    value={formData.deposit}
                    onChange={handleInputChange}
                    step="0.01"
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="voucher_number">{t('reservations.voucherNumber')}</label>
                  <input
                    type="text"
                    id="voucher_number"
                    name="voucher_number"
                    value={formData.voucher_number}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="referral_source">{t('reservations.referralSource')}</label>
                  <div className="referral-sources-dropdown-container" ref={referralDropdownRef}>
                    <button
                      type="button"
                      className="referral-sources-dropdown-button"
                      onClick={handleReferralDropdownToggle}
                    >
                      <span className="dropdown-button-text">
                        {selectedReferralSources.length === 0
                          ? t('reservations.selectReferralSources')
                          : selectedReferralSources.length === 1
                          ? selectedReferralSources[0]
                          : `${selectedReferralSources.length} ${t('reservations.sourcesSelected')}`}
                      </span>
                      <span className="dropdown-arrow">{showReferralDropdown ? '▲' : '▼'}</span>
                    </button>
                    
                    {showReferralDropdown && (
                      <div className="referral-sources-dropdown">
                        {referralSources.length === 0 ? (
                          <div className="no-referral-sources">
                            {t('reservations.noReferralSourcesAvailable')}
                          </div>
                        ) : (
                          referralSources.map(source => (
                            <label key={source.id} className="referral-source-item">
                              <input
                                type="checkbox"
                                checked={selectedReferralSources.includes(source.source_name)}
                                onChange={() => handleReferralSourceToggle(source.source_name)}
                              />
                              <span className="checkmark"></span>
                              <span className="source-name">{source.source_name}</span>
                            </label>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                  {selectedReferralSources.length > 0 && (
                    <div className="selected-referral-tags">
                      {selectedReferralSources.map(source => (
                        <span key={source} className="referral-tag">
                          {source}
                          <button
                            type="button"
                            className="remove-tag-btn"
                            onClick={() => handleReferralSourceToggle(source)}
                            title={t('common.remove')}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="venue">{t('reservations.venue')}</label>
                  <input
                    type="text"
                    id="venue"
                    name="venue"
                    value={formData.venue}
                    onChange={handleInputChange}
                    placeholder={t('reservations.venue')}
                  />
                </div>
              </div>
            </div>

            {/* Dress Measurements */}
            <div className="form-section">
              <h4>{t('reservations.dressMeasurements')}</h4>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="heel_height">{t('reservations.heelHeight')}</label>
                  <input
                    type="text"
                    id="heel_height"
                    name="heel_height"
                    value={formData.heel_height}
                    onChange={handleInputChange}
                    placeholder="e.g., 5cm"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="dress_length">{t('reservations.dressLength')}</label>
                  <input
                    type="text"
                    id="dress_length"
                    name="dress_length"
                    value={formData.dress_length}
                    onChange={handleInputChange}
                    placeholder="e.g., 150cm"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="crinoline_size">{t('reservations.crinolineSize')}</label>
                  <input
                    type="number"
                    id="crinoline_size"
                    name="crinoline_size"
                    value={formData.crinoline_size}
                    onChange={handleInputChange}
                    min="0"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="correction_notes" className="reservation-correction-notes-label" style={{ color: 'red' }}>
                    <span className="reservation-correction-notes-icon-wrap" aria-hidden="true">
                      <FontAwesomeIcon
                        icon={faTriangleExclamation}
                        className="reservation-correction-notes-icon"
                      />
                    </span>
                    <span className="reservation-correction-notes-text">
                      {t('reservations.correctionNotes')}
                    </span>
                  </label>
                  <textarea
                    id="correction_notes"
                    name="correction_notes"
                    value={formData.correction_notes}
                    onChange={handleInputChange}
                    rows="2"
                    placeholder={t('reservations.correctionNotesPlaceholder')}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="corset_notes">{t('reservations.corsetNotes')}</label>
                  <textarea
                    id="corset_notes"
                    name="corset_notes"
                    value={formData.corset_notes}
                    onChange={handleInputChange}
                    rows="2"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="strap_notes">{t('reservations.strapNotes')}</label>
                  <textarea
                    id="strap_notes"
                    name="strap_notes"
                    value={formData.strap_notes}
                    onChange={handleInputChange}
                    rows="2"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="neckline_notes">{t('reservations.necklineNotes')}</label>
                  <textarea
                    id="neckline_notes"
                    name="neckline_notes"
                    value={formData.neckline_notes}
                    onChange={handleInputChange}
                    rows="2"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="side_slit_notes">{t('reservations.sideslitNotes')}</label>
                  <textarea
                    id="side_slit_notes"
                    name="side_slit_notes"
                    value={formData.side_slit_notes}
                    onChange={handleInputChange}
                    rows="2"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="general_notes">{t('reservations.generalNotes')}</label>
                  <textarea
                    id="general_notes"
                    name="general_notes"
                    value={formData.general_notes}
                    onChange={handleInputChange}
                    rows="3"
                    placeholder={t('reservations.generalNotesPlaceholder')}
                  />
                </div>
              </div>
            </div>

            {/* Accessories */}
            <div className="form-section">
              <h4>{t('reservations.accessoriesAndExtras')}</h4>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="veil_size">{t('reservations.veilSize')}</label>
                  <input
                    type="text"
                    id="veil_size"
                    name="veil_size"
                    value={formData.veil_size}
                    onChange={handleInputChange}
                    placeholder="e.g., S, M, L"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="glove_size">{t('reservations.gloveSize')}</label>
                  <input
                    type="text"
                    id="glove_size"
                    name="glove_size"
                    value={formData.glove_size}
                    onChange={handleInputChange}
                    placeholder="e.g., S, M, L"
                  />
                </div>
              </div>

              <div className="accessories-grid">
                <div className="form-group">
                  <label htmlFor="hair_flower_count">{t('reservations.hairFlowers')}</label>
                  <input
                    type="number"
                    id="hair_flower_count"
                    name="hair_flower_count"
                    value={formData.hair_flower_count}
                    onChange={handleInputChange}
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="handbag_id">{t('reservations.handbag')}</label>
                  <select
                    id="handbag_id"
                    name="handbag_id"
                    value={formData.handbag_id}
                    onChange={handleInputChange}
                  >
                    <option value="">{t('reservations.noHandbag')}</option>
                    {getAccessoriesByType('handbag').map(accessory => (
                      <option key={accessory.id} value={accessory.id}>
                        {accessory.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="coat_id">{t('reservations.coat')}</label>
                  <select
                    id="coat_id"
                    name="coat_id"
                    value={formData.coat_id}
                    onChange={handleInputChange}
                  >
                    <option value="">{t('reservations.noCoat')}</option>
                    {getAccessoriesByType('coat').map(accessory => (
                      <option key={accessory.id} value={accessory.id}>
                        {accessory.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="wrap_id">{t('reservations.wrap')}</label>
                  <select
                    id="wrap_id"
                    name="wrap_id"
                    value={formData.wrap_id}
                    onChange={handleInputChange}
                  >
                    <option value="">{t('reservations.noWrap')}</option>
                    {getAccessoriesByType('wrap').map(accessory => (
                      <option key={accessory.id} value={accessory.id}>
                        {accessory.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="bolero_id">{t('reservations.bolero')}</label>
                  <select
                    id="bolero_id"
                    name="bolero_id"
                    value={formData.bolero_id}
                    onChange={handleInputChange}
                  >
                    <option value="">{t('reservations.noBolero')}</option>
                    {getAccessoriesByType('bolero').map(accessory => (
                      <option key={accessory.id} value={accessory.id}>
                        {accessory.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="tiara_id">{t('reservations.tiara')}</label>
                  <select
                    id="tiara_id"
                    name="tiara_id"
                    value={formData.tiara_id}
                    onChange={handleInputChange}
                  >
                    <option value="">{t('reservations.noTiara')}</option>
                    {getAccessoriesByType('tiara').map(accessory => (
                      <option key={accessory.id} value={accessory.id}>
                        {accessory.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="sash_id">{t('reservations.sash')}</label>
                  <select
                    id="sash_id"
                    name="sash_id"
                    value={formData.sash_id}
                    onChange={handleInputChange}
                  >
                    <option value="">{t('reservations.noSash')}</option>
                    {getAccessoriesByType('sash').map(accessory => (
                      <option key={accessory.id} value={accessory.id}>
                        {accessory.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="fur_jacket_id">{t('reservations.furJacket')}</label>
                  <select
                    id="fur_jacket_id"
                    name="fur_jacket_id"
                    value={formData.fur_jacket_id}
                    onChange={handleInputChange}
                  >
                    <option value="">{t('reservations.noFurJacket')}</option>
                    {getAccessoriesByType('fur_jacket').map(accessory => (
                      <option key={accessory.id} value={accessory.id}>
                        {accessory.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="veil_id">{t('reservations.veil')}</label>
                  <select
                    id="veil_id"
                    name="veil_id"
                    value={formData.veil_id}
                    onChange={handleInputChange}
                  >
                    <option value="">{t('reservations.noVeil')}</option>
                    {getAccessoriesByType('veil').map(accessory => (
                      <option key={accessory.id} value={accessory.id}>
                        {accessory.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="protective_bag_count">{t('reservations.protectiveBags')}</label>
                  <input
                    type="number"
                    id="protective_bag_count"
                    name="protective_bag_count"
                    value={formData.protective_bag_count}
                    onChange={handleInputChange}
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="small_protective_bag_count">{t('reservations.smallProtectiveBags')}</label>
                  <input
                    type="number"
                    id="small_protective_bag_count"
                    name="small_protective_bag_count"
                    value={formData.small_protective_bag_count}
                    onChange={handleInputChange}
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="hanger_hook_count">{t('reservations.hangerHooks')}</label>
                  <input
                    type="number"
                    id="hanger_hook_count"
                    name="hanger_hook_count"
                    value={formData.hanger_hook_count}
                    onChange={handleInputChange}
                    min="0"
                  />
                </div>
              </div>

              {/* Selected Accessories Preview */}
              {selectedAccessories.length > 0 && (
                <div className="selected-accessories-preview">
                  <h5>{t('reservations.selectedAccessories')}</h5>
                  <div className="selected-accessories-grid">
                    {selectedAccessories.map(accessory => (
                      <div key={accessory.field} className="selected-accessory-item">
                        <div className="accessory-image">
                          {accessory.image ? (
                            <img 
                              src={getThumbnailUrl(accessory.image)} 
                              alt={accessory.name}
                              className="accessory-thumbnail clickable"
                              onClick={() => handleAccessoryImageClick(accessory)}
                            />
                          ) : (
                            <div className="accessory-placeholder">🎀</div>
                          )}
                        </div>
                        <div className="accessory-info">
                          <div className="accessory-name">{accessory.name}</div>
                          <div className="accessory-type">{t(accessory.labelKey)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Contract Selection - Only in edit mode */}
            {mode === 'edit' && (
              <div className="form-section">
                <h4>{t('reservations.contractSelection')}</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="contract_selection">{t('reservations.selectContract')}</label>
                    {isLoadingContracts ? (
                      <div className="loading-text">{t('reservations.loadingContracts')}</div>
                    ) : (
                      <select
                        id="contract_selection"
                        name="contract_selection"
                        value={selectedContractId}
                        onChange={(e) => setSelectedContractId(e.target.value)}
                        disabled={!formData.transaction_type || availableContracts.length === 0}
                      >
                        {availableContracts.length === 0 ? (
                          <option value="">{t('reservations.noContractsAvailable')}</option>
                        ) : (
                          <>
                            <option value="">{t('reservations.selectContract')}</option>
                            {availableContracts.map(contract => (
                              <option key={contract.id} value={contract.id}>
                                {contract.title} {contract.is_active ? `(${t('reservations.active')})` : ''}
                              </option>
                            ))}
                          </>
                        )}
                      </select>
                    )}
                    {availableContracts.length === 0 && formData.transaction_type && !isLoadingContracts && (
                      <span style={{ fontSize: '0.875rem', color: '#718096', fontStyle: 'italic' }}>{t('reservations.noContractsForType', '', { type: formData.transaction_type })}</span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Payment Information - Only in edit mode, owner/admin */}
            {mode === 'edit' && (userRole === 'owner' || userRole === 'admin') && (
              <div className="form-section">
                <h4>{t('reservations.paymentInformation')}</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="paid_in_full"
                        checked={!!formData.paid_in_full}
                        onChange={handleInputChange}
                      />
                      <span className="checkmark"></span>
                      {t('reservations.paidInFull')}
                    </label>
                  </div>
                  <div className="form-group form-group-flex">
                    <label htmlFor="payment_comment">{t('reservations.paymentComment')}</label>
                    <textarea
                      id="payment_comment"
                      name="payment_comment"
                      value={formData.payment_comment}
                      onChange={handleInputChange}
                      placeholder={t('reservations.paymentComment')}
                      rows={2}
                    />
                  </div>
                </div>
              </div>
            )}

            </fieldset>

            {/* Delete Button - Only in edit mode */}
            {mode === 'edit' && (
              <div className="form-section">
                <div style={{ textAlign: 'center', display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <button
                    type="button"
                    className="sleek-button change-log"
                    onClick={handleToggleChangeLog}
                    disabled={submitting}
                  >
                    {isChangeLogVisible ? t('reservations.hideChangeLog') : t('reservations.viewChangeLog')}
                  </button>
                  {(userRole === 'owner' || userRole === 'admin') && (
                    <button 
                      type="button" 
                      className="sleek-button delete"
                      onClick={handleDeleteClick}
                      disabled={submitting}
                    >
                      {t('reservations.deleteReservation')}
                    </button>
                  )}

                </div>
              </div>
            )}

            {/* Change Log Section - Only in edit mode */}
            {mode === 'edit' && (
              <ChangeLog
                salonId={salonId}
                entityType="reservation"
                entityId={reservation?.id}
                getLocalizedFieldName={getLocalizedFieldName}
                isVisible={isChangeLogVisible}
                onToggle={handleToggleChangeLog}
                scrollContainerRef={modalBodyRef}
                titleKey="reservations.changeLogTitle"
                namespace="reservations"
              />
            )}
          </form>
        </div>
        
        <div className="modal-footer">
          <button 
            type="button" 
            className="salon-button secondary" 
            onClick={handleClose}
            disabled={submitting}
          >
            {t('common.cancel')}
          </button>
          
          {mode === 'edit' && (
            <button 
              type="button" 
              className="salon-button tertiary" 
              onClick={handlePrintContract}
              disabled={submitting || isLoadingContract || !selectedContractId}
            >
              {isLoadingContract ? t('reservations.loadingContract') : t('reservations.printContract')}
            </button>
          )}
          
          <button 
            type="submit" 
            className="salon-button primary" 
            onClick={handleSubmit}
            disabled={submitting || isEditLocked}
          >
            {submitting ? (mode === 'edit' ? t('reservations.updating') : t('reservations.creating')) : (mode === 'edit' ? t('common.saveChanges') : t('reservations.addReservation'))}
          </button>
        </div>

      </div>

      {/* Image Viewer Modal */}
      <ImageViewerModal
        isOpen={showImageViewer}
        onClose={() => setShowImageViewer(false)}
        images={viewerImages}
        currentIndex={currentImageIndex}
      />
    </div>
  );
};

export default AddEditReservationModal;
